import { useState, useEffect, useRef, useCallback } from "react";
import { GlassCard, N8N_BASE, FF, GF, INP, SEL, fmt, TabBar, SkeletonRows, StatCard } from "../shared";

const INVOICES_URL    = `${N8N_BASE}/qc-invoices-get`;
const REMITTANCE_URL  = `${N8N_BASE}/qc-remittance-create`;
const REMITTANCES_URL = `${N8N_BASE}/qc-get-remmitances`;

const SENDERS = [
  "Platform Media & Management Inc.",
  "Direct",
  "Other",
];

const STATUS_OPTS = [
  { value: "deposited",       label: "Deposited" },
  { value: "pending_deposit", label: "Pending Deposit" },
];

// YYYY-MM-DD (HTML date input) → DD/MM/YYYY (API)
function toApiDate(s) {
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}/${m}/${y}`;
  }
  return s;
}

// DD/MM/YYYY → display "Jun 4, 2026"
function fmtDisplayDate(s) {
  if (!s) return "—";
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/");
    return new Date(Number(y), Number(m) - 1, Number(d))
      .toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" });
  }
  return s;
}

function emptyLine() {
  return {
    invoice_number: "",
    brand:          "",
    amount:         "",
    invoice_total:  0,
    note:           "",
    _q:             "",
    _open:          false,
  };
}

// ── Status pill ───────────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const deposited = status === "deposited";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 9px", borderRadius: 20,
      fontSize: 11, fontWeight: 600, fontFamily: FF,
      background: deposited ? "rgba(52,199,89,0.1)"  : "rgba(255,149,0,0.1)",
      color:      deposited ? "rgba(30,130,60,0.9)"  : "rgba(160,90,0,0.9)",
      border:     deposited ? "0.5px solid rgba(52,199,89,0.22)" : "0.5px solid rgba(255,149,0,0.22)",
    }}>
      <span style={{
        width: 5, height: 5, borderRadius: "50%",
        background: deposited ? "rgba(52,199,89,0.85)" : "rgba(255,149,0,0.85)",
        flexShrink: 0,
      }} />
      {deposited ? "Deposited" : "Pending Deposit"}
    </span>
  );
}

// ── Remittance list row (expandable) ─────────────────────────────────────────
function RemittanceRow({ rem, expanded, onToggle, isLast }) {
  return (
    <div style={{ borderBottom: isLast ? "none" : "0.5px solid rgba(0,0,0,0.05)" }}>
      {/* Summary row */}
      <div
        onClick={onToggle}
        style={{
          display: "flex", alignItems: "center", gap: 12,
          padding: "14px 20px", cursor: "pointer",
          transition: "background 0.12s",
          background: expanded ? "rgba(255,255,255,0.38)" : "transparent",
          borderRadius: expanded ? "12px 12px 0 0" : 0,
        }}
        className="rem-row"
      >
        {/* Chevron */}
        <svg
          width="13" height="13" viewBox="0 0 24 24" fill="none"
          stroke="rgba(60,60,67,0.35)" strokeWidth="2.2" strokeLinecap="round"
          style={{ flexShrink: 0, transition: "transform 0.18s", transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
        >
          <polyline points="9,18 15,12 9,6"/>
        </svg>

        {/* ID + sender */}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1e", fontFamily: FF, letterSpacing: "-0.2px" }}>
              {rem.remittance_id}
            </span>
            <StatusPill status={rem.status} />
          </div>
          <div style={{ fontSize: 11, color: "rgba(60,60,67,0.45)", fontFamily: FF, marginTop: 2 }}>
            {rem.sender} · {rem.lines?.length ?? 0} invoice{rem.lines?.length !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Dates */}
        <div style={{ textAlign: "right", flexShrink: 0, display: "flex", flexDirection: "column", gap: 2 }}>
          <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
            Received {fmtDisplayDate(rem.date_received)}
          </div>
          <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
            Credit {fmtDisplayDate(rem.credit_date)}
          </div>
        </div>

        {/* Total */}
        <div style={{ fontSize: 15, fontWeight: 700, color: "#1c1c1e", fontFamily: FF, letterSpacing: "-0.3px", flexShrink: 0, minWidth: 80, textAlign: "right" }}>
          ${fmt(Number(rem.total_cad) || 0)}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{
          padding: "0 20px 16px",
          background: "rgba(255,255,255,0.28)",
          borderRadius: "0 0 12px 12px",
        }}>
          {/* Divider */}
          <div style={{ height: "0.5px", background: "rgba(0,0,0,0.06)", marginBottom: 12 }} />

          {/* Meta row */}
          <div style={{ display: "flex", gap: 24, marginBottom: 14, flexWrap: "wrap" }}>
            {[
              { label: "Sender",        value: rem.sender },
              { label: "Date Received", value: fmtDisplayDate(rem.date_received) },
              { label: "Credit Date",   value: fmtDisplayDate(rem.credit_date) },
              { label: "Status",        value: <StatusPill status={rem.status} /> },
            ].map(({ label, value }) => (
              <div key={label}>
                <div style={{ fontSize: 9, fontWeight: 600, color: "rgba(60,60,67,0.32)", letterSpacing: "0.6px", textTransform: "uppercase", fontFamily: FF, marginBottom: 4 }}>
                  {label}
                </div>
                <div style={{ fontSize: 12, color: "#1c1c1e", fontFamily: FF, fontWeight: 500 }}>
                  {value}
                </div>
              </div>
            ))}
            {rem.notes && (
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: "rgba(60,60,67,0.32)", letterSpacing: "0.6px", textTransform: "uppercase", fontFamily: FF, marginBottom: 4 }}>
                  Notes
                </div>
                <div style={{ fontSize: 12, color: "rgba(60,60,67,0.7)", fontFamily: FF }}>
                  {rem.notes}
                </div>
              </div>
            )}
          </div>

          {/* Invoice lines table */}
          {rem.lines && rem.lines.length > 0 && (
            <div style={{
              background: "rgba(255,255,255,0.5)",
              border: "0.5px solid rgba(255,255,255,0.82)",
              borderRadius: 10, overflow: "hidden",
            }}>
              {/* Table header */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                padding: "8px 14px",
                borderBottom: "0.5px solid rgba(0,0,0,0.05)",
              }}>
                {["Invoice #", "Brand", "Amount"].map((h) => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 600, color: "rgba(60,60,67,0.32)", letterSpacing: "0.6px", textTransform: "uppercase", fontFamily: FF }}>
                    {h}
                  </div>
                ))}
              </div>
              {/* Table rows */}
              {rem.lines.map((ln, i) => (
                <div key={i} style={{
                  display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                  padding: "9px 14px",
                  borderBottom: i < rem.lines.length - 1 ? "0.5px solid rgba(0,0,0,0.04)" : "none",
                  alignItems: "center",
                }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1c1c1e", fontFamily: FF }}>
                    {ln.invoice_number}
                  </div>
                  <div style={{ fontSize: 12, color: "rgba(60,60,67,0.7)", fontFamily: FF }}>
                    {ln.brand}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1c1c1e", fontFamily: FF }}>
                    ${fmt(Number(ln.amount) || 0)}
                  </div>
                </div>
              ))}
              {/* Subtotal */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                padding: "9px 14px",
                borderTop: "0.5px solid rgba(0,0,0,0.07)",
                background: "rgba(0,0,0,0.02)",
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "rgba(60,60,67,0.45)", fontFamily: FF, gridColumn: "1 / 3" }}>
                  Total
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#1c1c1e", fontFamily: FF }}>
                  ${fmt(Number(rem.total_cad) || 0)}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Searchable invoice combobox ────────────────────────────────────────────────
function InvoiceCombobox({ invoices, line, onSelect, onSearchChange, onClose }) {
  const ref = useRef(null);

  const filtered = line._q
    ? invoices.filter((inv) =>
        inv.invoice_number?.toLowerCase().includes(line._q.toLowerCase()) ||
        inv.brand?.toLowerCase().includes(line._q.toLowerCase()) ||
        inv.campaign_name?.toLowerCase().includes(line._q.toLowerCase())
      ).slice(0, 12)
    : invoices.slice(0, 12);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  return (
    <div ref={ref} style={{ flex: 2, minWidth: 140, position: "relative" }}>
      <input
        className="g-input"
        style={{ ...INP, fontSize: 13 }}
        value={line._q !== "" ? line._q : line.invoice_number}
        placeholder="Search invoice…"
        onChange={(e) => onSearchChange(e.target.value)}
        onFocus={() => onSearchChange(line._q || line.invoice_number || "")}
      />
      {line._open && filtered.length > 0 && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 200,
          background: "rgba(250,250,252,0.98)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
          border: "0.5px solid rgba(200,200,210,0.7)",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          overflow: "hidden", maxHeight: 240, overflowY: "auto",
        }}>
          {filtered.map((inv) => (
            <div
              key={inv.row_number || inv.invoice_number}
              className="drop-item"
              onMouseDown={(e) => { e.preventDefault(); onSelect(inv); }}
              style={{
                padding: "9px 14px", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#1c1c1e", fontFamily: FF }}>
                  {inv.invoice_number}
                </div>
                <div style={{ fontSize: 11, color: "rgba(60,60,67,0.5)", fontFamily: FF, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {inv.campaign_name} · {inv.brand}
                </div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "rgba(60,60,67,0.55)", fontFamily: FF, flexShrink: 0 }}>
                ${fmt(Number(inv.invoice_total) || 0)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function RemittancePage() {
  const [tab, setTab] = useState("list");

  // ── List tab state ──────────────────────────────────────────────────────────
  const [remittances,  setRemittances]  = useState([]);
  const [remLoading,   setRemLoading]   = useState(true);
  const [remError,     setRemError]     = useState(null);
  const [expandedId,   setExpandedId]   = useState(null);
  const remFetched = useRef(false);

  // ── New tab state ───────────────────────────────────────────────────────────
  const [invoices,   setInvoices]   = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const invFetched = useRef(false);

  // Fire both fetches in parallel on mount so switching to "New" tab is instant
  useEffect(() => {
    // Remittances
    if (!remFetched.current) {
      remFetched.current = true;
      (async () => {
        setRemLoading(true); setRemError(null);
        try {
          const r = await fetch(REMITTANCES_URL);
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const d = await r.json();
          const list = Array.isArray(d) ? d : (d.remittances || []);
          list.sort((a, b) => {
            const parse = (s) => {
              if (!s) return 0;
              const [dd, mm, yyyy] = s.split("/");
              return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getTime();
            };
            return parse(b.date_received) - parse(a.date_received);
          });
          setRemittances(list);
        } catch (e) {
          setRemError(e.message || "Failed to load remittances.");
        }
        setRemLoading(false);
      })();
    }

    // Invoices — fire in background so "New Remittance" tab is ready immediately
    if (!invFetched.current) {
      invFetched.current = true;
      (async () => {
        try {
          const r = await fetch(INVOICES_URL);
          if (!r.ok) throw new Error();
          const d = await r.json();
          const list = Array.isArray(d) ? (d[0]?.invoices || d) : (d.invoices || []);
          setInvoices(
            (Array.isArray(list) ? list : []).filter(
              (i) => i.status === "unpaid" || i.status === "pending_payment"
            )
          );
        } catch {}
        setInvLoading(false);
      })();
    }
  }, []); // eslint-disable-line

  // Refresh list after successful submit
  const refreshList = () => {
    remFetched.current = false;
    invFetched.current = false;
    setRemittances([]);
    setTab("list");
    // Re-trigger fetch
    remFetched.current = false;
    (async () => {
      setRemLoading(true); setRemError(null);
      try {
        const r = await fetch(REMITTANCES_URL);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        const list = Array.isArray(d) ? d : (d.remittances || []);
        list.sort((a, b) => {
          const parse = (s) => {
            if (!s) return 0;
            const [dd, mm, yyyy] = s.split("/");
            return new Date(Number(yyyy), Number(mm) - 1, Number(dd)).getTime();
          };
          return parse(b.date_received) - parse(a.date_received);
        });
        setRemittances(list);
      } catch (e) { setRemError(e.message || "Failed to load remittances."); }
      setRemLoading(false);
    })();
  };

  const [form, setForm] = useState({
    sender:        "Platform Media & Management Inc.",
    date_received: "",
    credit_date:   "",
    status:        "deposited",
    notes:         "",
  });
  const [lines,      setLines]      = useState([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [result,     setResult]     = useState(null);

  // ── Line helpers ─────────────────────────────────────────────────────────────
  const updateLine = (idx, patch) =>
    setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  const handleSelect = useCallback((idx, inv) => {
    const total = Number(inv.invoice_total) || 0;
    updateLine(idx, {
      invoice_number: inv.invoice_number,
      brand:          inv.brand || "",
      invoice_total:  total,
      amount:         total > 0 ? String(total) : "",
      note:           "",
      _q:             "",
      _open:          false,
    });
  }, []); // eslint-disable-line

  const handleSearchChange = useCallback((idx, q) => {
    updateLine(idx, { _q: q, _open: true, invoice_number: "" });
  }, []); // eslint-disable-line

  const handleClose = useCallback((idx) => {
    updateLine(idx, { _open: false });
  }, []); // eslint-disable-line

  const handleAmount = (idx, value) => {
    const line      = lines[idx];
    const amt       = Number(value) || 0;
    const total     = Number(line.invoice_total) || 0;
    const isPartial = total > 0 && amt > 0 && amt < total;
    const remaining = total - amt;
    updateLine(idx, {
      amount: value,
      note:   isPartial ? `Partial — $${fmt(remaining)} remaining` : "",
    });
  };

  const addLine    = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (idx) => setLines((ls) => ls.filter((_, i) => i !== idx));

  const grandTotal = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const handleSubmit = async () => {
    if (!form.date_received || !form.credit_date) {
      setResult({ type: "error", msg: "Date Received and Credit Date are required." });
      return;
    }
    const validLines = lines.filter((l) => l.invoice_number && Number(l.amount) > 0);
    if (!validLines.length) {
      setResult({ type: "error", msg: "Add at least one invoice line with an amount." });
      return;
    }
    setSubmitting(true); setResult(null);
    try {
      const payload = {
        sender:        form.sender,
        date_received: toApiDate(form.date_received),
        credit_date:   toApiDate(form.credit_date),
        total_cad:     grandTotal,
        status:        form.status,
        notes:         form.notes,
        lines: validLines.map((l) => {
          const amt        = Number(l.amount);
          const total      = Number(l.invoice_total);
          const is_partial = total > 0 && amt < total;
          return { invoice_number: l.invoice_number, brand: l.brand, amount: amt, invoice_total: total, is_partial, note: l.note };
        }),
      };
      const r = await fetch(REMITTANCE_URL, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const res = await r.json();
      setResult({ type: "success", msg: `${res.remittance_id || "Remittance"} saved — ${res.message || "success"}` });
      setForm({ sender: "Platform Media & Management Inc.", date_received: "", credit_date: "", status: "deposited", notes: "" });
      setLines([emptyLine()]);
      // Refresh list on next visit
      refreshList();
    } catch (e) {
      setResult({ type: "error", msg: e.message || "Submit failed. Check connection." });
      setTab("new"); // stay on new tab if error
    } finally { setSubmitting(false); }
  };

  // ── Shared styles ─────────────────────────────────────────────────────────────
  const panel = {
    background: "rgba(255,255,255,0.45)",
    border:     "0.5px solid rgba(255,255,255,0.78)",
    borderRadius: 16,
  };

  const colLabel = {
    fontSize: 10, fontWeight: 600,
    color: "rgba(60,60,67,0.38)",
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    fontFamily: FF,
  };

  const totalDeposited = remittances
    .filter((r) => r.status === "deposited")
    .reduce((s, r) => s + (Number(r.total_cad) || 0), 0);

  return (
    <div style={{ height: "calc(100vh - 24px)", display: "flex", flexDirection: "column", fontFamily: FF }}>
      <GlassCard style={{ maxWidth: "none", height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>

        {/* ── Fixed header ───────────────────────────────────────────────────── */}
        <div style={{ flexShrink: 0, padding: "22px 28px 16px", borderBottom: "0.5px solid rgba(0,0,0,0.06)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10,
              background: "rgba(255,255,255,0.55)",
              border: "0.5px solid rgba(255,255,255,0.88)",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="rgba(50,60,90,0.6)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="5" width="20" height="14" rx="2"/>
                <line x1="2" y1="10" x2="22" y2="10"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.4px" }}>Remittances</div>
              <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", marginTop: 1 }}>Payments received from platforms & brands</div>
            </div>
          </div>

          {/* Tabs */}
          <TabBar
            tabs={[
              { id: "list", label: "All Remittances" },
              { id: "new",  label: "New Remittance"  },
            ]}
            active={tab}
            onChange={setTab}
          />
        </div>

        {/* ── Scrollable content ───────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 0, padding: "22px 28px 36px" }}>

          {/* ════════════════ LIST TAB ════════════════ */}
          {tab === "list" && (
            <>
              {remLoading && (
                <>
                  {/* 3-column stat strip skeleton — matches exact layout of loaded state */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                    {[0,1,2].map(i => (
                      <div key={i} style={{
                        flex: 1, height: 70, borderRadius: 16,
                        background: "rgba(255,255,255,0.45)",
                        border: "0.5px solid rgba(255,255,255,0.78)",
                        animation: "skpulse 1.4s ease-in-out infinite",
                        animationDelay: `${i * 0.08}s`,
                      }} />
                    ))}
                  </div>
                  <SkeletonRows count={5} height={62} />
                </>
              )}
              {remError && (
                <div style={{ padding: "40px 0", textAlign: "center", color: "rgba(160,40,30,0.8)", fontSize: 13, fontFamily: FF }}>
                  {remError}
                </div>
              )}
              {!remLoading && !remError && remittances.length === 0 && (
                <div style={{ padding: "40px 0", textAlign: "center", color: "rgba(60,60,67,0.35)", fontSize: 13, fontFamily: FF }}>
                  No remittances yet.{" "}
                  <span style={{ color: "rgba(50,90,200,0.65)", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => setTab("new")}>
                    Create one →
                  </span>
                </div>
              )}
              {!remLoading && !remError && remittances.length > 0 && (
                <>
                  {/* Summary strip */}
                  <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
                    <StatCard label="Total Received" value={`$${fmt(remittances.reduce((s, r) => s + (Number(r.total_cad) || 0), 0))}`} sub={`${remittances.length} remittances`} />
                    <StatCard label="Deposited" value={`$${fmt(totalDeposited)}`} accent="rgba(235,252,240,0.65)" />
                    <StatCard label="Pending Deposit" value={`$${fmt(remittances.filter(r => r.status === "pending_deposit").reduce((s, r) => s + (Number(r.total_cad) || 0), 0))}`} />
                  </div>

                  {/* List */}
                  <div style={{ ...panel, overflow: "hidden" }}>
                    {remittances.map((rem, i) => (
                      <RemittanceRow
                        key={rem.remittance_id}
                        rem={rem}
                        expanded={expandedId === rem.remittance_id}
                        onToggle={() => setExpandedId(expandedId === rem.remittance_id ? null : rem.remittance_id)}
                        isLast={i === remittances.length - 1}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}

          {/* ════════════════ NEW TAB ════════════════ */}
          {tab === "new" && (
            <>
              {/* Payment details */}
              <div style={{ ...panel, padding: "20px 20px 18px", marginBottom: 14 }}>
                <div style={{ ...colLabel, marginBottom: 16 }}>Payment Details</div>

                <div className="form-grid-2" style={{ marginBottom: 14 }}>
                  <GF label="Sender" required>
                    <select className="g-select" style={{ ...SEL, fontSize: 13 }}
                      value={form.sender}
                      onChange={(e) => setForm((f) => ({ ...f, sender: e.target.value }))}>
                      {SENDERS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </GF>
                  <GF label="Status">
                    <select className="g-select" style={{ ...SEL, fontSize: 13 }}
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                      {STATUS_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </GF>
                </div>

                <div className="form-grid-2" style={{ marginBottom: 14 }}>
                  <GF label="Date Received" required>
                    <input className="g-input" type="date" style={{ ...INP, fontSize: 13 }}
                      value={form.date_received}
                      onChange={(e) => setForm((f) => ({ ...f, date_received: e.target.value }))} />
                  </GF>
                  <GF label="Credit Date" required>
                    <input className="g-input" type="date" style={{ ...INP, fontSize: 13 }}
                      value={form.credit_date}
                      onChange={(e) => setForm((f) => ({ ...f, credit_date: e.target.value }))} />
                  </GF>
                </div>

                <GF label="Notes">
                  <input className="g-input" style={{ ...INP, fontSize: 13 }}
                    placeholder="Optional notes…"
                    value={form.notes}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
                </GF>
              </div>

              {/* Invoice lines */}
              <div style={{ ...panel, padding: "20px 20px 18px", marginBottom: 14 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={colLabel}>Invoice Lines</div>
                  <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
                    {invLoading ? "Loading…" : `${invoices.length} unpaid invoices`}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, padding: "0 2px", marginBottom: 8 }}>
                  <div style={{ ...colLabel, flex: 2 }}>Invoice #</div>
                  <div style={{ ...colLabel, flex: 1.5 }}>Brand</div>
                  <div style={{ ...colLabel, flex: 1 }}>Amount</div>
                  <div style={{ ...colLabel, flex: 2 }}>Note</div>
                  <div style={{ width: 30 }} />
                </div>

                {lines.map((line, idx) => {
                  const amt       = Number(line.amount) || 0;
                  const total     = Number(line.invoice_total) || 0;
                  const isPartial = total > 0 && amt > 0 && amt < total;

                  return (
                    <div key={idx} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
                      <InvoiceCombobox
                        invoices={invoices}
                        line={line}
                        onSelect={(inv) => handleSelect(idx, inv)}
                        onSearchChange={(q) => handleSearchChange(idx, q)}
                        onClose={() => handleClose(idx)}
                      />
                      <div style={{ flex: 1.5, minWidth: 0 }}>
                        <input className="g-input" style={{ ...INP, fontSize: 13 }}
                          placeholder="Brand" value={line.brand}
                          onChange={(e) => updateLine(idx, { brand: e.target.value })} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ position: "relative" }}>
                          <span style={{
                            position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                            fontSize: 13, color: "rgba(60,60,67,0.4)", fontFamily: FF, pointerEvents: "none",
                          }}>$</span>
                          <input className="g-input"
                            style={{ ...INP, fontSize: 13, paddingLeft: 22, border: isPartial ? "0.5px solid rgba(255,149,0,0.45)" : INP.border }}
                            type="number" min="0" step="0.01" placeholder="0.00"
                            value={line.amount}
                            onChange={(e) => handleAmount(idx, e.target.value)} />
                        </div>
                        {isPartial && (
                          <div style={{ fontSize: 10, color: "rgba(160,90,0,0.7)", fontFamily: FF, marginTop: 3, paddingLeft: 2 }}>
                            of ${fmt(total)}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 2, minWidth: 0 }}>
                        <input className="g-input" style={{ ...INP, fontSize: 13 }}
                          placeholder="Note…" value={line.note}
                          onChange={(e) => updateLine(idx, { note: e.target.value })} />
                      </div>
                      <button onClick={() => removeLine(idx)} disabled={lines.length === 1}
                        style={{
                          width: 30, height: 42, flexShrink: 0, borderRadius: 8,
                          border: "0.5px solid rgba(255,255,255,0.75)",
                          background: "rgba(255,255,255,0.42)",
                          color: lines.length === 1 ? "rgba(60,60,67,0.18)" : "rgba(200,50,40,0.55)",
                          cursor: lines.length === 1 ? "default" : "pointer",
                          fontSize: 18, fontWeight: 300,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>×</button>
                    </div>
                  );
                })}

                <button onClick={addLine} style={{
                  marginTop: 6, display: "flex", alignItems: "center", gap: 6,
                  padding: "8px 14px", borderRadius: 10,
                  border: "0.5px solid rgba(255,255,255,0.78)",
                  background: "rgba(255,255,255,0.42)",
                  color: "rgba(60,60,67,0.55)", fontSize: 12, fontWeight: 500,
                  cursor: "pointer", fontFamily: FF,
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  Add Line
                </button>
              </div>

              {/* Total + submit */}
              <div style={{ ...panel, padding: "18px 20px 20px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
                  <div>
                    <div style={{ ...colLabel, marginBottom: 4 }}>Total</div>
                    <div style={{ fontSize: 11, color: "rgba(60,60,67,0.38)", fontFamily: FF }}>
                      {lines.filter((l) => l.invoice_number).length} invoice{lines.filter((l) => l.invoice_number).length !== 1 ? "s" : ""} · auto-calculated
                    </div>
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.7px", fontFamily: FF }}>
                    ${fmt(grandTotal)}
                  </div>
                </div>

                {result && (
                  <div style={{
                    marginBottom: 14, padding: "10px 14px", borderRadius: 10,
                    fontSize: 13, fontWeight: 500, fontFamily: FF,
                    display: "flex", alignItems: "center", gap: 7,
                    ...(result.type === "success"
                      ? { background: "rgba(52,199,89,0.09)", color: "#1e7a3c", border: "1px solid rgba(52,199,89,0.18)" }
                      : { background: "rgba(200,50,40,0.07)", color: "rgba(160,40,30,0.9)", border: "1px solid rgba(200,50,40,0.15)" }),
                  }}>
                    {result.type === "success"
                      ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20,6 9,17 4,12"/></svg>
                      : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
                    {result.msg}
                  </div>
                )}

                <button className="submit-btn" onClick={handleSubmit} disabled={submitting}
                  style={{
                    width: "100%", padding: "14px",
                    background: "rgba(20,22,30,0.88)", border: "none", borderRadius: 12,
                    color: "white", fontSize: 14, fontWeight: 600,
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.55 : 1, fontFamily: FF,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    transition: "opacity 0.15s",
                  }}>
                  {submitting ? "Submitting…" : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                      Submit Remittance
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </GlassCard>
    </div>
  );
}
